import { getDcApiBase, getLawvoApiBase, isHashDataEnabled } from './config';
import { decodeData } from './hashHelper';
import { clearAllAuthStorage, formatBearerToken, getDcAccessToken, getLawvoAccessToken } from './lawvoStorage';
export class ApiAuthError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'ApiAuthError';
  }
}
type FetchOptions = RequestInit & {
  skipAuthRedirect?: boolean;
};
function handleUnauthorized(skipRedirect?: boolean): void {
  if (skipRedirect) return;
  clearAllAuthStorage();
  if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
}
async function parseResponseError(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  let message = `API error: ${response.status} ${response.statusText}`;
  try {
    const json = JSON.parse(text);
    const nested = json?.message?.raw?.message || json?.message || json?.error?.message;
    if (typeof nested === 'string' && nested) message = nested;
  } catch {
    if (text) message += ` — ${text.slice(0, 200)}`;
  }
  return message;
}
function decodeLawvoResponseBody<T>(body: T, path: string): T {
  if (!isHashDataEnabled()) return body;
  const authPath = path.split('?')[0];
  if (authPath === '/authentication') return body;
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if ('total' in record && 'data' in record) {
      record.data = decodeData(record.data, path);
      return body;
    }
    if ('data' in record) {
      record.data = decodeData(record.data, path);
      return body;
    }
    return body;
  }
  if (typeof body === 'string') {
    return decodeData(body, path) as T;
  }
  return body;
}
export async function lawvoFetch(path: string, options: FetchOptions = {}): Promise<Response> {
  const {
    skipAuthRedirect,
    headers: extraHeaders,
    ...rest
  } = options;
  const token = getLawvoAccessToken();
  const headers = new Headers(extraHeaders);
  if (!headers.has('Content-Type') && rest.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', formatBearerToken(token));
  }
  headers.set('x-lawvo-client-platform', 'web');
  const response = await fetch(`${getLawvoApiBase()}${path}`, {
    ...rest,
    headers
  });
  if (response.status === 401) {
    handleUnauthorized(skipAuthRedirect);
    throw new ApiAuthError(await parseResponseError(response), 401);
  }
  return response;
}
export async function lawvoFetchJson<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const response = await lawvoFetch(path, options);
  if (!response.ok) {
    throw new Error(await parseResponseError(response));
  }
  if (response.status === 204) return undefined as T;
  const body = (await response.json()) as T;
  return decodeLawvoResponseBody(body, path);
}
export async function dcFetch(path: string, options: FetchOptions = {}): Promise<Response> {
  const {
    skipAuthRedirect,
    headers: extraHeaders,
    ...rest
  } = options;
  const token = getDcAccessToken();
  const headers = new Headers(extraHeaders);
  if (!headers.has('Content-Type') && rest.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', formatBearerToken(token));
  }
  const url = path.startsWith('http') ? path : `${getDcApiBase()}${path.startsWith('/') ? path : `/${path}`}`;
  const response = await fetch(url, {
    ...rest,
    headers
  });
  if (response.status === 401) {
    handleUnauthorized(skipAuthRedirect);
    throw new ApiAuthError(await parseResponseError(response), 401);
  }
  return response;
}
export async function dcFetchJson<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const response = await dcFetch(path, options);
  if (!response.ok) {
    throw new Error(await parseResponseError(response));
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}